import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import {
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import classNames from 'classnames'
import * as R from 'ramda'
import { formatDate } from '@pbt/pbt-ui-components/src/utils/dateUtils'

import { ConversationStatus, MessageStatus } from '~/constants/communications'
import { fetchConversationMessagesList } from '~/store/actions/conversations'
import { getChatMessages } from '~/store/reducers/conversationMessages'
import type { Conversation } from '~/types'

import MessageDateCell from './MessageDateCell'
import MessageRow from './MessageRow'
import SubjectCell from './SubjectCell'

import styles from './ConversationRow.module.scss'

const ConversationRow = ({
  conversation,
  isLoading,
  LoadingSkeleton,
}: {
  LoadingSkeleton: React.ReactElement
  conversation: Conversation
  isLoading: boolean
}) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const messages = useSelector(getChatMessages(conversation?.messages || []))
  const isExpandable = messages.length > 1
  const appointmentName =
    R.prop('name', conversation.event?.businessAppointmentType) || ''
  const appointmentDate = R.prop('scheduledStartDatetime', conversation.event)

  const { id, title, status } = conversation

  const isUnread = status === ConversationStatus.UNREAD

  useEffect(() => {
    if (id) {
      dispatch(fetchConversationMessagesList(0, 5, id))
    }
  }, [id])

  if (isLoading) {
    return LoadingSkeleton
  }
  const [firstMessage] = messages
  const messageContent = firstMessage?.body || ''
  const isDraft = firstMessage?.status === MessageStatus.DRAFT

  return (
    <>
      <TableRow
        className={classNames(styles.tableRow, {
          [styles.unreadTableRow]: isUnread,
        })}
        data-testid="conversation-row"
      >
        <TableCell
          className={classNames(styles.expandCell, {
            [styles.noBorder]: open,
          })}
        >
          {isExpandable && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
          )}
        </TableCell>
        <MessageDateCell
          lastMessageDate={conversation.lastMessageDate}
          noBottomBorder={open}
        />
        <SubjectCell
          conversationId={id}
          isDraft={isDraft}
          isUnread={isUnread}
          message={messageContent}
          noBottomBorder={open}
          subject={title}
        />
        <TableCell
          className={classNames(styles.tableCell, {
            [styles.noBorder]: open,
          })}
        >
          {appointmentName || appointmentDate
            ? `${appointmentName} ${formatDate(appointmentDate)}`
            : '—'}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell className={classNames(styles.messageRow)} colSpan={4}>
          <Collapse
            unmountOnExit
            className={classNames({
              [styles.expandedRow]: open,
            })}
            in={open}
          >
            <Table size="small">
              <TableBody>
                {messages.map((message) => (
                  <MessageRow
                    conversation={conversation}
                    key={message.id}
                    message={message}
                  />
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default ConversationRow
